import React, { useState } from 'react';
import { Save, Loader2, Heart, Users } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, Dependente, Familia } from '../../shared/atlasApi';
import ListaEditavel, { inputCls, labelCls } from '../../shared/ListaEditavel';
import FeedbackBanner, { Feedback } from '../../shared/FeedbackBanner';

interface Props {
  user: AtlasUser;
  colaborador: Colaborador;
  podeEditar: boolean;
  onSalvo: () => void;
}

const ESTADOS_CIVIS = ['Solteiro(a)', 'Casado(a)', 'União estável', 'Divorciado(a)', 'Separado(a)', 'Viúvo(a)'];

const AbaFamilia: React.FC<Props> = ({ user, colaborador, podeEditar, onSalvo }) => {
  const e = colaborador.enriquecimento || {};
  const f0 = e.familia || {};
  const [dependentes, setDependentes] = useState<Dependente[]>(
    Array.isArray(e.dependentes) ? e.dependentes.map(d => ({ ...d })) : [],
  );
  const [familia, setFamilia] = useState<Familia>({
    estado_civil: f0.estado_civil || '',
    conjuge_nome: f0.conjuge_nome || '',
    conjuge_data_nascimento: (f0.conjuge_data_nascimento || '').slice(0, 10),
    conjuge_telefone: f0.conjuge_telefone || '',
    qtd_filhos: f0.qtd_filhos ?? null,
    observacoes: f0.observacoes || '',
  });
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const setF = (patch: Partial<Familia>) => setFamilia(p => ({ ...p, ...patch }));

  const salvar = async () => {
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.putFamilia(user, colaborador.funccodi, familia);
      await atlasApi.putDependentes(user, colaborador.funccodi, dependentes);
      setFeedback({ tipo: 'ok', msg: 'Dados de família salvos com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const ro = !podeEditar;

  return (
    <div className="space-y-5">
      <FeedbackBanner feedback={feedback} />

      {/* Estado civil / cônjuge */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <Heart className="w-4 h-4" /> Núcleo familiar
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Estado civil</label>
            <select className={inputCls} disabled={ro} value={familia.estado_civil || ''} onChange={ev => setF({ estado_civil: ev.target.value })}>
              <option value="">—</option>
              {ESTADOS_CIVIS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Quantidade de filhos</label>
            <input
              type="number"
              min={0}
              className={inputCls}
              disabled={ro}
              value={familia.qtd_filhos ?? ''}
              onChange={ev => setF({ qtd_filhos: ev.target.value === '' ? null : Number(ev.target.value) })}
            />
          </div>
          <div>
            <label className={labelCls}>Nome do cônjuge / companheiro(a)</label>
            <input className={inputCls} disabled={ro} value={familia.conjuge_nome || ''} onChange={ev => setF({ conjuge_nome: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Telefone do cônjuge</label>
            <input className={inputCls} disabled={ro} value={familia.conjuge_telefone || ''} onChange={ev => setF({ conjuge_telefone: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Nascimento do cônjuge</label>
            <input type="date" className={inputCls} disabled={ro} value={(familia.conjuge_data_nascimento || '').slice(0, 10)} onChange={ev => setF({ conjuge_data_nascimento: ev.target.value })} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Observações sobre a família</label>
          <textarea
            className={`${inputCls} min-h-[70px] resize-y`}
            disabled={ro}
            value={familia.observacoes || ''}
            onChange={ev => setF({ observacoes: ev.target.value })}
          />
        </div>
      </section>

      <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider pt-1">
        <Users className="w-4 h-4" /> Dependentes
      </div>

      <ListaEditavel<Dependente>
        itens={dependentes}
        onChange={setDependentes}
        novoItem={() => ({ nome: '', parentesco: '', data_nascimento: '', cpf: '', ir: false, plano_saude: false })}
        rotuloAdd="adicionar dependente"
        vazioMsg="Nenhum dependente cadastrado."
        podeEditar={podeEditar}
        renderLinha={(dep, atualizar) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Nome</label>
              <input className={inputCls} disabled={!podeEditar} value={dep.nome || ''} onChange={ev => atualizar({ nome: ev.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Parentesco</label>
              <input className={inputCls} disabled={!podeEditar} value={dep.parentesco || ''} onChange={ev => atualizar({ parentesco: ev.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Data de nascimento</label>
              <input type="date" className={inputCls} disabled={!podeEditar} value={(dep.data_nascimento || '').slice(0, 10)} onChange={ev => atualizar({ data_nascimento: ev.target.value })} />
            </div>
            <div>
              <label className={labelCls}>CPF</label>
              <input className={inputCls} disabled={!podeEditar} value={dep.cpf || ''} onChange={ev => atualizar({ cpf: ev.target.value })} />
            </div>
            <div className="flex items-center gap-4 md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" disabled={!podeEditar} checked={!!dep.ir} onChange={ev => atualizar({ ir: ev.target.checked })} className="accent-purple-600" />
                Dependente de IR
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" disabled={!podeEditar} checked={!!dep.plano_saude} onChange={ev => atualizar({ plano_saude: ev.target.checked })} className="accent-purple-600" />
                Plano de saúde
              </label>
            </div>
          </div>
        )}
      />

      {podeEditar && (
        <div className="flex justify-end pt-1">
          <button
            onClick={salvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar família
          </button>
        </div>
      )}
    </div>
  );
};

export default AbaFamilia;
